/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { TouchableOpacity, View } from "react-native";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import {
  FadeInOutView,
  LogoutModal,
  ReportIssueView,
  Text,
  TutorialModal,
} from "UpduoLibrary";
import {
  upduoPrimaryBlack,
  upduoPrimaryBlue,
  upduoLightestGray,
} from "config/UpduoColors";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import NavWidget from "./NavWidget";
import { dashboardStyles } from "./shared_components";
import UserDashboardWidget from "./UserWidget";
import SessionCompletionWidget from "./SessionCompletionWidget";
import ActivityLogWidget from "./ActivityLogWidget";
import LeaderboardWidget from "./LeaderboardWidget";
import AccountSettings from "components/SettingsRoute/AccountSettings";
import PhotoModal from "UpduoLibrary/PhotoModal";
import { instantiateMemberUser } from "config/ResetState";
import DeactivateSettings from "components/SettingsRoute/DeactivateSettings";
import DeactivateModal from "components/SettingsRoute/DeactivateModal";

export default function UserDashboardRoute() {
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showOverview, setShowOverview] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showReportIssue, setShowReportIssue] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [deactivateModal, setDeactivateModal] = useState("none");

  const dispatch = useDispatch();

  const selfObject = useSelector((state: RootState) => state.selfObject);

  const _handleOnClickLogout = () => {
    UpduoAnalytics.logEvent("user-dashboard-logout");
    setLogoutModalVisible(true);
  };

  const _handleShowTutorial = () => {
    setShowTutorial(true);
  };

  const _handleShowOverview = () => {
    setShowOverview(true);
    setShowSettings(false);
    setShowReportIssue(false);
  };

  const _handleShowSettings = () => {
    setShowOverview(false);
    setShowSettings(true);
    setShowReportIssue(false);
  };

  const _handleShowReportIssue = () => {
    setShowOverview(false);
    setShowSettings(false);
    setShowReportIssue(true);
  };

  const _handleSuccessfulSubmission = () => {
    // Add delay for latest photoSubmitSuccessCounter value
    setTimeout(() => {
      // Submission is successful only if both small and large formats are uploaded
      dispatch(ActionCreators.setProfilePicSet(true));
    }, 500);
    setShowPhotoModal(false);
    instantiateMemberUser();
  };

  const _handleSubmitIssue = () => {};

  return (
    <div
      style={{
        paddingLeft: 60,
        paddingRight: 60,
        paddingTop: 32,
        paddingBottom: 52,
        boxSizing: "border-box",
      }}
      className={"fill-height-except-navbar"}
    >
      <View
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Text
          isHeading={true}
          style={{
            fontSize: 22,
            marginBottom: 16,
            color: upduoPrimaryBlack,
          }}
        >
          {showOverview ? "Overview" : "Settings"}
        </Text>

        <View
          style={{
            display: "flex",
            flexDirection: "row",
            flexGrow: 1,
            alignItems: "stretch",
          }}
        >
          <NavWidget
            style={{
              flexBasis: 230,
              flexGrow: 1,
              flexShrink: 1,
            }}
            overviewIsActive={showOverview}
            settingsIsActive={showSettings}
            reportIssueIsActive={showReportIssue}
            handleLogout={_handleOnClickLogout}
            handleShowTutorial={_handleShowTutorial}
            handleShowOverview={_handleShowOverview}
            handleShowSettings={_handleShowSettings}
            handleShowReportIssue={_handleShowReportIssue}
          />
          {showOverview ? (
            <>
              <View
                style={{
                  flexBasis: 740,
                  flexGrow: 1,
                  flexShrink: 1,
                  marginHorizontal: 16,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                }}
              >
                <View
                  style={{
                    flexBasis: 336,
                    flexGrow: 1,
                    flexShrink: 1,
                    marginBottom: 20,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "stretch",
                  }}
                >
                  <UserDashboardWidget
                    style={{
                      flexBasis: 230,
                      flexGrow: 1,
                      flexShrink: 1,
                      marginRight: 16,
                    }}
                    setShowSettings={setShowSettings}
                  />
                  <SessionCompletionWidget
                    style={{ flexBasis: 494, flexGrow: 1, flexShrink: 1 }}
                  />
                </View>
                <ActivityLogWidget
                  style={[
                    dashboardStyles.dashboardWidget,
                    { flexBasis: 317, flexGrow: 1, flexShrink: 1 },
                  ]}
                />
              </View>
              {selfObject.organizationShowDistrict && selfObject.districtId ? (
                <LeaderboardWidget
                  style={[
                    dashboardStyles.dashboardWidget,
                    { flexBasis: 327, flexGrow: 1, flexShrink: 1 },
                  ]}
                />
              ) : null}
            </>
          ) : showSettings ? (
            <View
              style={{
                flexBasis: 1088,
                flexGrow: 1,
                flexShrink: 1,
                marginHorizontal: 16,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              <FadeInOutView
                style={{
                  backgroundColor: "white",
                  borderRadius: 12,
                  overflow: "hidden",
                  borderStyle: "solid",
                  borderColor: upduoLightestGray,
                  borderWidth: 1,
                  display: "flex",
                  flexDirection: "column",
                  padding: 32,
                  minWidth: 423,
                  height: "100%",
                  justifyContent: "space-between",
                }}
              >
                <AccountSettings
                  onShowPhotoModal={() => setShowPhotoModal(true)}
                />
                <View>
                  <TouchableOpacity
                    onPress={() => {
                      window.open(
                        `${process.env.REACT_APP_SERVER_URL}/terms/`,
                        "_blank"
                      );
                    }}
                  >
                    <Text
                      style={{
                        color: upduoPrimaryBlue,
                        fontSize: 16,
                      }}
                    >
                      {"Terms of Service"}
                    </Text>
                  </TouchableOpacity>

                  <View
                    style={{
                      borderColor: upduoLightestGray,
                      borderWidth: 0.5,
                      marginVertical: 20,
                      width: "100%",
                    }}
                  ></View>
                  <TouchableOpacity
                    onPress={() => {
                      window.open(
                        `${process.env.REACT_APP_SERVER_URL}/privacy/`,
                        "_blank"
                      );
                    }}
                  >
                    <Text
                      style={{
                        color: upduoPrimaryBlue,
                        fontSize: 16,
                      }}
                    >
                      {"Privacy Policy"}
                    </Text>
                  </TouchableOpacity>
                </View>
                <DeactivateSettings setActiveModal={setDeactivateModal} />
              </FadeInOutView>
            </View>
          ) : (
            <View
              style={{
                flexBasis: 1088,
                flexGrow: 1,
                flexShrink: 1,
                marginHorizontal: 16,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              <FadeInOutView
                style={{
                  backgroundColor: "white",
                  borderRadius: 12,
                  overflow: "hidden",
                  borderStyle: "solid",
                  borderColor: upduoLightestGray,
                  borderWidth: 1,
                  display: "flex",
                  flexDirection: "column",
                  padding: 32,
                  minWidth: 423,
                  height: "100%",
                  justifyContent: "space-between",
                }}
              >
                <View style={{ maxWidth: 560 }}>
                  <Text
                    isHeading={true}
                    style={{ fontSize: 20, marginBottom: 30 }}
                  >
                    {"Report an Issue"}
                  </Text>
                  <ReportIssueView
                    marginHorizontal={0}
                    alignButton={"flex-start"}
                    onFinish={_handleSubmitIssue}
                  />
                </View>
              </FadeInOutView>
            </View>
          )}
        </View>
      </View>
      <LogoutModal
        isVisible={logoutModalVisible}
        onClose={() => {
          setLogoutModalVisible(false);
        }}
        history={history}
      />
      <TutorialModal
        visible={showTutorial}
        onClose={() => {
          UpduoAnalytics.logEvent("user-dashboard-showtutorial");
          setShowTutorial(false);
        }}
      />
      <PhotoModal
        isVisible={showPhotoModal}
        onDismiss={() => setShowPhotoModal(false)}
        updatePhoto={_handleSuccessfulSubmission}
        userHasPhoto={true}
      />
      <DeactivateModal
        isVisible={deactivateModal !== "none"}
        onClose={() => {
          setDeactivateModal("none");
        }}
        deactivate={deactivateModal === "deactivate"}
      />
    </div>
  );
}
